import { useState, type KeyboardEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatFileSize } from '@brimo/shared';
import { Skeleton, Toggle, useToast } from '../../../ds/components';
import { clsx } from '../../../ds/clsx';
import { ApiError, api, errorMessage } from '../../../lib/api';
import { WizardButton } from '../ui';
import { organizationKeys } from './api';
import { FrameModal } from './FrameModal';
import type { Domain, ItMailDraft } from './types';
import styles from './ItMailDialog.module.css';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_MESSAGE = 'Bitte eine gültige E-Mail-Adresse angeben.';

export interface ItMailDialogProps {
  open: boolean;
  onClose: () => void;
  /** wizard: Admin 07 (700 px, „Kopie an mich" switch) · settings: Einstellungen 23 (680 px, „Kopie an"). */
  variant: 'wizard' | 'settings';
  onSent?: ((domain: Domain) => void) | undefined;
}

/** „Anleitung an die IT senden": the draft of `GET …/domain/it-mail`, editable, sent with the guide PDF. */
export function ItMailDialog({ open, onClose, variant, onSent }: ItMailDialogProps) {
  const draft = useQuery({
    queryKey: organizationKeys.itMail,
    queryFn: () => api<ItMailDraft>('/settings/organization/domain/it-mail'),
    enabled: open,
    staleTime: 0,
    gcTime: 0,
  });
  return (
    <FrameModal
      open={open}
      onClose={onClose}
      eyebrow="Domain einrichten"
      title="Anleitung an die IT senden"
      width={variant === 'wizard' ? 700 : 680}
      top={variant === 'wizard' ? 0 : 60}
      offset={variant === 'wizard' ? 36 : 0}
    >
      {draft.isPending ? (
        <Skeleton variant="block" height={360} />
      ) : draft.isError ? (
        <span className={styles.error} role="alert">
          {errorMessage(draft.error)}
        </span>
      ) : (
        <MailForm draft={draft.data} variant={variant} onClose={onClose} onSent={onSent} />
      )}
    </FrameModal>
  );
}

function MailForm({
  draft,
  variant,
  onClose,
  onSent,
}: {
  draft: ItMailDraft;
  variant: 'wizard' | 'settings';
  onClose: () => void;
  onSent: ((domain: Domain) => void) | undefined;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [recipients, setRecipients] = useState<string[]>(draft.to);
  const [entry, setEntry] = useState('');
  const [entryError, setEntryError] = useState<string | null>(null);
  const [ccMe, setCcMe] = useState(draft.ccMe);
  const [subject, setSubject] = useState(draft.subject);
  const [text, setText] = useState(draft.text);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  function addEntry(): boolean {
    const value = entry.trim().replace(/[,;]$/, '').toLowerCase();
    if (value === '') return true;
    if (!EMAIL_PATTERN.test(value)) {
      setEntryError(EMAIL_MESSAGE);
      return false;
    }
    if (!recipients.includes(value)) setRecipients([...recipients, value]);
    setEntry('');
    setEntryError(null);
    return true;
  }

  function onEntryKey(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
      event.preventDefault();
      addEntry();
    } else if (event.key === 'Backspace' && entry === '' && recipients.length > 0) {
      setRecipients(recipients.slice(0, -1));
    }
  }

  async function send() {
    if (!addEntry()) return;
    const to = entry.trim() === '' ? recipients : [...recipients, entry.trim().toLowerCase()];
    if (to.length === 0) {
      setEntryError('Bitte mindestens eine Adresse angeben.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const domain = await api<Domain>('/settings/organization/domain/it-mail', {
        method: 'POST',
        body: { to, ccMe, subject, text },
      });
      queryClient.setQueryData(organizationKeys.domain, domain);
      toast.toast({ message: `Anleitung an ${to.join(', ')} gesendet.` });
      onSent?.(domain);
      onClose();
    } catch (failure) {
      if (failure instanceof ApiError && Object.keys(failure.fields).length > 0) {
        setError(Object.values(failure.fields)[0] ?? failure.message);
      } else {
        setError(errorMessage(failure));
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <div className={styles.grid}>
        <label className={styles.label} htmlFor="it-mail-to">
          An
        </label>
        <div>
          <div className={clsx(styles.chips, entryError && styles.chipsInvalid)}>
            {recipients.map((address) => (
              <span key={address} className={styles.chip}>
                {address}
                <button
                  type="button"
                  className={styles.chipRemove}
                  aria-label={`${address} entfernen`}
                  onClick={() => setRecipients(recipients.filter((item) => item !== address))}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              id="it-mail-to"
              type="email"
              className={styles.chipInput}
              placeholder={variant === 'wizard' ? 'weitere Adresse' : 'weitere Adresse hinzufügen'}
              value={entry}
              aria-invalid={entryError ? true : undefined}
              aria-describedby={entryError ? 'it-mail-to-error' : undefined}
              onChange={(event) => {
                setEntry(event.target.value);
                setEntryError(null);
              }}
              onKeyDown={onEntryKey}
              onBlur={() => void addEntry()}
            />
          </div>
          {entryError ? (
            <div id="it-mail-to-error" className={styles.error} role="alert">
              {entryError}
            </div>
          ) : null}
        </div>
        {variant === 'wizard' ? (
          <>
            <span className={styles.label} id="it-mail-cc">
              Kopie an mich
            </span>
            <div className={styles.copy}>
              <Toggle size="large" checked={ccMe} onChange={setCcMe} label="Kopie an mich" labelHidden />
              <span>{draft.ccAddress}</span>
            </div>
          </>
        ) : (
          <>
            <label className={styles.label} htmlFor="it-mail-cc-address">
              Kopie an
            </label>
            <input
              id="it-mail-cc-address"
              className={clsx(styles.input, styles.inputReadOnly)}
              value={draft.ccAddress}
              readOnly
            />
          </>
        )}
        <label className={styles.label} htmlFor="it-mail-subject">
          Betreff
        </label>
        <input
          id="it-mail-subject"
          className={styles.input}
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
        />
      </div>
      <label className={styles.srOnly} htmlFor="it-mail-text">
        Text der Mail
      </label>
      <textarea
        id="it-mail-text"
        className={styles.body}
        value={text}
        onChange={(event) => setText(event.target.value)}
      />
      <div className={styles.attachmentRow}>
        <span className={styles.attachment}>
          {draft.attachment.name} · {formatFileSize(draft.attachment.size)}
        </span>
        <span className={styles.note}>{draft.note}</span>
      </div>
      {error ? (
        <div className={styles.error} role="alert">
          {error}
        </div>
      ) : null}
      <div className={styles.actions}>
        <WizardButton variant="secondary" size="small" onClick={onClose}>
          Abbrechen
        </WizardButton>
        <WizardButton variant="primary" size="small" loading={sending} onClick={() => void send()}>
          Senden
        </WizardButton>
      </div>
    </>
  );
}
